import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { filter, map, take } from 'rxjs/operators';
import { duration } from 'moment/moment';
import { BaseHttpService } from '@nimbus-library/features/shared/services/base-http/base-http.service';
import { IOrganizationUnitSelectListItemViewModel } from '@nimbus-global/features/shared/services/api/organization-unit/domain/i-organization-unit-select-list-item.view-model';
import { UserPermissionsService } from '@nimbus-global/features/permissions/services/domain/user-permissions.service';
import { OrganizationUnitTypeEnum } from '@nimbus-global/features/shared/services/api/organization-unit/domain/organization-unit-type.enum';
import { IOrganizationUnitSelectListItemTempModel } from '@nimbus-global/features/shared/services/api/organization-unit/domain/i-organization-unit-select-list-item.temp-model';
import { ToOrganizationUnitSelectListModelMapper } from '@nimbus-global/features/shared/services/api/organization-unit/domain/to-organization-unit-select-list-model.mapper';
import { CacheService, IEntityCacheService } from '@nimbus-library/features/cache';

@Injectable({
  providedIn: 'root',
})
export class OrganizationUnitTenantCacheService {
  private readonly tenantOrganizationUnitsCacheTimeout = duration(10, 'minutes');
  private readonly tenantOrganizationUnitsCache: IEntityCacheService<IOrganizationUnitSelectListItemViewModel[]>;

  public constructor(
    protected baseHttpService: BaseHttpService,
    protected readonly userPermissionService: UserPermissionsService,
    cacheService: CacheService,
  ) {
    this.tenantOrganizationUnitsCache = cacheService.for<IOrganizationUnitSelectListItemViewModel[]>('tenantOrganizationUnitsCache', {
      cacheMaxAge: this.tenantOrganizationUnitsCacheTimeout,
    });
  }

  public getOrganizationUnits(tenantId: string): Observable<IOrganizationUnitSelectListItemViewModel[]> {
    return this.getByTenant$(tenantId).pipe(
      filter((result) => !!result),
      take(1),
      map((result) => result),
    );
  }

  public getForTree(
    tenantId: string,
    kinds?: OrganizationUnitTypeEnum[],
    canManageOnly: boolean = false,
  ): Observable<IOrganizationUnitSelectListItemTempModel[]> {
    return this.getOrganizationUnits(tenantId).pipe(
      map((x) =>
        new ToOrganizationUnitSelectListModelMapper().forTree(
          x.filter((a) => !kinds || (kinds.includes(a.kind) && (!canManageOnly || a.canManage))),
        ),
      ),
    );
  }

  private getByTenant$(tenantId: string): Observable<IOrganizationUnitSelectListItemViewModel[]> {
    const organizationUnits = this.tenantOrganizationUnitsCache.getInfo(tenantId);
    if (organizationUnits && organizationUnits.value && organizationUnits.validForCache) {
      return of(organizationUnits.value);
    }
    return this.getAuthorizedOrganizationUnits(tenantId).pipe(
      take(1),
      map((values) => {
        this.tenantOrganizationUnitsCache.set(tenantId, values);
        return values;
      }),
    );
  }

  private getAuthorizedOrganizationUnits(tenantId: string): Observable<IOrganizationUnitSelectListItemViewModel[]> {
    return this.baseHttpService.get(`facade/v1/common-portal/authorized-organization-units?tenantId=${tenantId}`);
  }
}
